import { useState } from 'react';
import { Menu, Settings, ChevronLeft, ChevronRight, X, Bell, Store, Eye } from 'lucide-react';
import type { Avisos } from './useAvisos';
import type { ClienteApp } from './api';
import { EntradaLoja } from './EntradaLoja';
import { EspelhoContatos } from './EspelhoContatos';
import { MarcaPrestador } from './MarcaPrestador';
import { TrocarSenhaApp } from './TrocarSenhaApp';

// O menu ☰ do aplicativo do cliente e a tela de Configuracoes (DA-534).
//
// Pedido dele, 14/08/2026: *"Podemos criar um menu de 3 tracinho padrao de celular
// e colocar la dentro uma opcao Config, e dentro dela colocar a opcao de desmarcar
// aviso de mensagens?"*.
//
// ⚠️ **Isto tirou a faixa "Avisos ligados" do chat.** Ela era permanente e repetia
// a cada abertura uma informacao ja entendida, comendo uma linha de uma tela de
// celular. Aviso que esta funcionando nao precisa se anunciar; o que precisa de
// lugar fixo e' o CONTROLE — e lugar de controle e' dentro do menu.
//
// ⚠️ **Nao fecha por toque no fundo** — convencao da casa pra qualquer painel que
// cobre a tela. E tem DOIS caminhos de saida visiveis (o X no topo e o Fechar no
// rodape), porque no celular nao existe Esc e um unico caminho e' ponto unico de
// falha ([DA-458]).

const COBRE = '#b45309';

export function MenuApp({ avisos, cliente, aoAtualizar }: {
  avisos: Avisos; cliente: ClienteApp; aoAtualizar: (c: ClienteApp) => void;
}): React.ReactElement {
  const [aberto, setAberto] = useState(false);
  const [vista, setVista] = useState<'menu' | 'config' | 'loja' | 'espelho'>('menu');

  // ⚠️ **Contas a Pagar e Orcamentos SAIRAM daqui em 18/08/2026** ([DA-575]) — foram
  // pra HOME, que virou a porta de entrada do app. Nao repor: as duas em dois lugares
  // seria porta dupla pra mesma area, e o ☰ ficaria disputando com o menu da tela.
  //
  // O que sobrou aqui tem uma coisa em comum: **e' ajuste, nao trabalho.** A marca do
  // prestador e o aviso de mensagens se configuram uma vez e raramente se revisitam; a
  // porta da equipe e' de uso do Ruan. Area que ele abre todo dia mora na home.

  // A porta da equipe so existe pra quem e' ADMINISTRADOR do aplicativo (migration
  // 0224). O padrao de toda conta nova e' `usuario`, entao o cliente comum nem sabe
  // que ela existe.
  //
  // ⛔ Isto ESCONDE, nao tranca — quem passa continua sendo decidido pelo login do
  // sistema e pelo device-trust. O freio de tentativa do `POST /api/auth/login` foi
  // posto na mesma leva justamente porque o endereco segue respondendo a quem o
  // digitar na mao.
  const ehAdministrador = cliente.papel === 'administrador';
  // ⛔ Dentro do espelho as duas portas de admin somem: o servidor ja recusa as
  // duas ([DA-634]), e oferecer o que vai ser negado e' o defeito da [DA-593].
  // Quem esta espelhando sai pela faixa do topo, que e' o unico caminho daqui.
  const podeAdministrar = ehAdministrador && !cliente.espelho;

  function abrir(): void {
    setVista('menu');
    setAberto(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={abrir}
        aria-label="Menu"
        style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}
      >
        <Menu size={22} />
      </button>

      {aberto && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, backgroundColor: '#f7f7f7', color: '#111', display: 'flex', flexDirection: 'column' }}>
          <header style={{ backgroundColor: COBRE, color: '#fff', padding: '12px 8px 12px 12px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {vista !== 'menu' ? (
              <button
                type="button"
                onClick={() => setVista('menu')}
                aria-label="Voltar"
                style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
              >
                <ChevronLeft size={22} />
              </button>
            ) : (
              <span style={{ width: 6 }} />
            )}
            <div style={{ flex: 1, fontSize: 17, fontWeight: 800, letterSpacing: 0.3 }}>
              {vista === 'config' ? 'Configuracoes'
                : vista === 'loja' ? 'Entrar como loja'
                : vista === 'espelho' ? 'Acessar como Contato'
                : 'Menu'}
            </div>
            <button type="button" onClick={() => setAberto(false)} aria-label="Fechar" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <X size={22} />
            </button>
          </header>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {vista === 'menu' ? (
              <>
                <button
                  type="button"
                  onClick={() => setVista('config')}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '16px 16px', backgroundColor: '#fff',
                    borderBottom: '1px solid #e7e5e4', fontSize: 16, textAlign: 'left', color: '#111',
                  }}
                >
                  <Settings size={20} style={{ color: COBRE, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>Config</span>
                  <ChevronRight size={18} style={{ color: '#9ca3af' }} />
                </button>

                {/* ⚠️ A porta da equipe mora aqui, e este e' hoje o UNICO caminho pra
                    ela. Ela ja existiu tambem na tela de entrada, visivel pra quem
                    abrisse `/app` sem conta nenhuma; saiu de la em 14/08/2026 por
                    decisao do Ruan, junto com a criacao do papel.

                    Ela vive no menu, e nao na entrada, porque o Ruan usa o app com a
                    conta de cliente dele: quando a porta so existia na tela de
                    entrada, ele abriu e respondeu *"Nao vi onde acessar"* — pra
                    chegar la teria que SAIR primeiro, e caminho que exige logout nao
                    existe pra quem ja esta dentro. */}
                {podeAdministrar && (
                  <button
                    type="button"
                    onClick={() => setVista('loja')}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '16px 16px', backgroundColor: '#fff',
                      borderBottom: '1px solid #e7e5e4', fontSize: 16, textAlign: 'left', color: '#111',
                    }}
                  >
                    <Store size={20} style={{ color: COBRE, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      Entrar como loja
                      <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                        Ver e responder todos os clientes
                      </span>
                    </span>
                    <ChevronRight size={18} style={{ color: '#9ca3af' }} />
                  </button>
                )}

                {/* "Acessar como Contato" ([DA-634]) — ver o app pelos olhos de
                    quem o usa, so pra olhar. Fica ABAIXO da porta da equipe de
                    proposito: aquela e' trabalho (responder cliente), esta e'
                    conferencia, e a ordem do menu diz o que se usa mais. */}
                {podeAdministrar && (
                  <button
                    type="button"
                    onClick={() => setVista('espelho')}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '16px 16px', backgroundColor: '#fff',
                      borderBottom: '1px solid #e7e5e4', fontSize: 16, textAlign: 'left', color: '#111',
                    }}
                  >
                    <Eye size={20} style={{ color: COBRE, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>
                      Acessar como Contato
                      <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                        Ver o aplicativo como o cliente ve
                      </span>
                    </span>
                    <ChevronRight size={18} style={{ color: '#9ca3af' }} />
                  </button>
                )}
              </>
            ) : vista === 'loja' && podeAdministrar ? (
              <EntradaLoja aoVoltar={() => setVista('menu')} />
            ) : vista === 'espelho' && podeAdministrar ? (
              <EspelhoContatos aoVoltar={() => setVista('menu')} />
            ) : (
              <>
                {/* A marca vem ANTES do aviso de mensagens: e' o que muda o papel que
                    ele entrega, enquanto o aviso e' ajuste do aparelho. */}
                <MarcaPrestador cliente={cliente} aoAtualizar={aoAtualizar} soLeitura={!!cliente.espelho} />
                <LinhaAvisos avisos={avisos} espelho={!!cliente.espelho} />
                {/* A senha fica por ULTIMO de proposito: e' o ajuste mais raro dos
                    tres — a marca se configura ao chegar, o aviso se revisita, e a
                    senha so se toca depois de um reset da loja. */}
                <TrocarSenhaApp cliente={cliente} aoAtualizar={aoAtualizar} soLeitura={!!cliente.espelho} />
              </>
            )}
          </div>

          {/* Segundo caminho de saida, no rodape — ver o cabecalho deste arquivo. */}
          <div style={{ padding: 14, flexShrink: 0, borderTop: '1px solid #e7e5e4', backgroundColor: '#fff' }}>
            <button
              type="button"
              onClick={() => setAberto(false)}
              style={{ width: '100%', height: 48, borderRadius: 10, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 15.5, fontWeight: 700 }}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** A linha "Avisos de mensagens" dentro de Configuracoes. */
function LinhaAvisos({ avisos, espelho = false }: { avisos: Avisos; espelho?: boolean }): React.ReactElement {
  const { situacao, ocupado, erro, detalhe, ligar, desligar } = avisos;
  const ligado = situacao === 'ligado';

  // ⚠️ Chave travada SEMPRE diz por que (`CLAUDE.md` raiz, § UI). Um controle
  // cinza e mudo e' beco sem saida: a pessoa toca, nada acontece, e nao ha como
  // adivinhar o que falta.
  //
  // ⚠️ **O espelho vem PRIMEIRO na cadeia**, e o motivo nao e' ordem alfabetica: o
  // aviso e' propriedade do APARELHO, nao da conta ([DA-534]). Espelhando, o que a
  // chave leria e' o estado do MEU celular, e liga-la inscreveria o MEU aparelho
  // nos avisos daquele cliente. So leitura, e dizendo por que ([DA-634]).
  const motivo =
    espelho ? 'Modo espelho — o aviso é deste aparelho, não da conta que você está vendo.'
    : situacao === 'apurando' ? 'Conferindo...'
    : situacao === 'ensinar-iphone' ? 'No iPhone, os avisos so funcionam com o atalho na tela de inicio. Adicione o AppCel pelo botao Compartilhar do Safari e abra por ele.'
    : situacao === 'bloqueado' ? 'Voce bloqueou os avisos deste site no navegador. Libere nas configuracoes do site e volte aqui.'
    : situacao === 'indisponivel' ? 'Este aparelho nao recebe avisos.'
    : null;

  const travado = motivo !== null || ocupado;

  return (
    <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #e7e5e4' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px' }}>
        <Bell size={20} style={{ color: COBRE, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16 }}>Aviso de mensagens</div>
          {/* O significado vem do TEXTO, nunca da cor sozinha — verde/vermelho nao
              servem de sinal nesta casa (daltonismo). */}
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
            {ocupado ? 'Um instante...' : ligado ? 'Ligado neste aparelho' : 'Desligado'}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={ligado}
          aria-label="Aviso de mensagens"
          disabled={travado}
          onClick={() => void (ligado ? desligar() : ligar())}
          style={{
            width: 52, height: 31, borderRadius: 999, flexShrink: 0,
            backgroundColor: ligado ? COBRE : '#d6d3d1',
            opacity: travado ? 0.45 : 1,
            position: 'relative', transition: 'background-color .15s',
          }}
        >
          <span style={{
            position: 'absolute', top: 3, left: ligado ? 24 : 3,
            width: 25, height: 25, borderRadius: 999, backgroundColor: '#fff',
            transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
          }} />
        </button>
      </div>

      <div style={{ padding: '0 16px 14px 48px', fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
        {motivo ?? 'Avisamos voce quando a loja responder, mesmo com o aplicativo fechado.'}
      </div>

      {erro && (
        <div style={{ margin: '0 16px 14px', padding: '9px 11px', borderRadius: 8, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
          {erro}
          {/* A medicao crua junto do erro, igual a faixa do chat: e' o que vem no print
              quando o cliente diz "nao funcionou". Ver `push.ErroPush.detalhe`. */}
          {detalhe && (
            <div style={{ marginTop: 5, fontSize: 10.5, color: '#78716c', fontFamily: 'monospace' }}>
              {detalhe}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
